<template>
  <div
    class="product-gallery"
    :class="[
      `product-gallery--${responsiveLayout}`,
      {
        'single-media': singleMedia,
      },
    ]"
    role="region"
    :aria-label="$string('product.gallery.label', { replace: { product: product.title } })"
  >
    <div class="product-gallery__main-container">
      <transition name="fade">
        <button
          v-if="showZoomButton"
          class="product-gallery__zoom"
          type="button"
          @click.prevent="handleZoomClick"
        >
          <icon-plus />

          <span
            class="text-other-label-s text-other-label-l-desktop"
            v-text="$string('product.gallery.zoom')"
          />
        </button>
      </transition>

      <product-gallery-carousel
        class="product-gallery__main-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.main"
        :layout="responsiveLayout"
        :loading="loading"
        :media="media"
        :video-muted="videoMuted"
        namespace="gallery"
        :product="product"
        :sizes="sizes"
        type="main"
        :zoom="zoom"
        @update-current-index="handleUpdateCurrentIndex"
        @video-mute-update="handleVideoMuteUpdate"
      />
    </div>

    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container"
    >
      <product-gallery-carousel
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.thumbnails"
        :layout="responsiveLayout"
        :loading="loading"
        :media="media"
        min-max="100-300"
        namespace="gallery"
        :product="product"
        sizes="88px"
        type="thumbnails"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          :media="media"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          :video-muted="videoMuted"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
          @video-mute-update="handleVideoMuteUpdate"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductGalleryCarousel from '~async/product-gallery-carousel/product-gallery-carousel'

import IconPlus from '~icons/directional-navigation/plus.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    IconPlus,
    Overlay,
    ProductGalleryCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-').every((part) => ['grid', 'horizontal', 'tabs', 'vertical'].includes(part))
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, null, Object],
      default: () => ({}),
    },
    zoom: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
  },

  data() {
    return {
      videoMuted: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute carousel configs.
     * - The aim here is to convert the responsive layout into the breakpoints
     *   object supported by Splide.
     * @returns {Object}
     */
    config() {
      const mainConfigs = {
        sharedMobile: {
          autoWidth: true,
          gap: 'var(--gallery-spacing)',
          padding: {
            left: this.singleMedia ? 0 : 'var(--gallery-layout-margin)',
            right: this.singleMedia ? 0 : 'var(--gallery-layout-margin)',
          },
        },
        sharedDesktop: {
          autoWidth: false,
          gap: 0,
          padding: 0,
          perPage: 1,
        },
        grid: {
          arrows: false,
          destroy: true,
          pagination: false,
        },
        horizontal: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
        tabs: {
          arrows: true,
          destroy: false,
          pagination: true,
        },
        vertical: {
          arrows: true,
          destroy: false,
          pagination: true,
        },
      }

      const thumbnailsConfigs = {
        shared: {
          arrows: this.product.media?.length > 6,
          drag: true,
          focus: 0,
          pagination: false,
          perMove: 1,
          perPage: 6,
          trimSpace: true,
          type: 'slide',
        },
        horizontal: {
          gap: 'var(--spacing-m)',
        },
        vertical: {
          direction: 'ttb',
          fixedHeight: '90px',
          gap: 'var(--spacing-m)',
          height: 'calc((90px + var(--spacing-m)) * 6)',
        },
      }

      /**
       * Set configs.
       * - No difference between mobile and desktop layouts.
       */
      if (!this.layout.includes('-')) {
        const config = {
          main: {
            ...mainConfigs[this.layout],
            ...mainConfigs.sharedMobile,
            breakpoints: {
              [breakpoints.l]: {
                ...mainConfigs.sharedDesktop,
              },
            },
          },
          thumbnails: {
            ...thumbnailsConfigs.shared,
            ...thumbnailsConfigs[this.layout],
          },
        }

        return config
      }

      /**
       * Set mobile and desktop layouts independently.
       */
      const mobileLayout = this.layout.split('-')[0]
      const desktopLayout = this.layout.split('-')[1]

      const config = {
        main: {
          ...mainConfigs.sharedMobile,
          ...mainConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...mainConfigs.sharedDesktop,
              ...mainConfigs[desktopLayout],
            },
          },
        },
        thumbnails: {
          ...thumbnailsConfigs.shared,
          ...thumbnailsConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...thumbnailsConfigs[desktopLayout],
            },
          },
        },
      }

      /**
       * Return config.
       */
      return config
    },

    /**
     * Compute product/variant media.
     * @returns {Array}
     */
    media() {
      return this.variant && this.variant.metafields.images.length
        ? this.variant.metafields.images
        : this.product.media
    },

    /**
     * Compute gallery has only 1 media.
     * @returns {Boolean}
     */
    singleMedia() {
      return this.media?.length === 1
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return this.responsiveLayout === 'horizontal' || this.responsiveLayout === 'vertical'
    },

    /**
     * Compute whether to show zoom button.
     * - Hidden on mobile, during loading, or when zoom is disabled.
     * - Only shown for supported media types (image, video).
     * @returns {Boolean}
     */
    showZoomButton() {
      const currentMedia = this.media?.length
        ? this.media[this.currentIndex]
        : false

      const isZoomableMedia = currentMedia &&
        (
          currentMedia?.mediaType === 'image' ||
          currentMedia?.mediaType === 'video' ||
          currentMedia?.mediaType === 'external_video' ||
          !currentMedia?.mediaType
        )

      return (
        !this.isMobile &&
        this.zoom &&
        !this.loading &&
        isZoomableMedia
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 283px`
    },
  },

  watch: {

    /**
     * Watch variant to show matching image if it exists, unless variant images
     * are populated from metafields.
     */
    variant() {
      if (!this.variant) {
        return
      }

      if (!this.variant || this.variant.metafields.images.length) {
        return
      }

      /**
       * Match media (either media or images array) with associated variant.
       */
      const matchedImageIndex = this.product?.media?.findIndex((media) => {
        return media.variant === this.variant.id
      })

      if (matchedImageIndex < 0) {
        return
      }

      this.$emit('update-current-index', matchedImageIndex)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Updates local video mute state.
     * @param {boolean} muted
     */
    handleVideoMuteUpdate(muted) {
      this.videoMuted = muted
    },

    /**
     * Handle zoom click.
     */
    handleZoomClick() {
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
// Disable unused layout stylesheets
// @import './product-gallery-grid';
@import './product-gallery-tabs';
// @import './product-gallery-horizontal';
@import './product-gallery-vertical';
</style>
